import type { CSSProperties } from "react";
import { cn } from "../lib/cn";
import { ThriveLeaf } from "./thrive-leaf";

/**
 * ThriveLogo: the full "leaf in a white puck, surrounded by pulsing teal rings"
 * composition used across thrive-plan (timeline hero, home, empty / celebration
 * states). Ported from thrive-plan's ThriveLogoWithCircles but refactored to:
 *
 *  1. Render the leaf inline via <ThriveLeaf /> (no dependency on a public
 *     asset, so it works in SSR, Workers, and iframe embeds alike).
 *  2. Use named size variants with distinct circle sizes per variant, the
 *     original conflated `small` and `medium` to the same config.
 *  3. Keep the `pulse-outward` keyframe animation driven by ui-tokens so the
 *     rings ripple identically to the source.
 *
 * The four concentric rings are a signature micro-interaction: each ring uses
 * a teal border that gets slightly more saturated as it moves inward, and
 * their `pulse-outward` animation is staggered (delays 0s / 0.8s / 1.6s /
 * 2.4s) so the rings feel like breath rather than a synchronous pulse.
 */

export interface ThriveLogoProps {
  size?: "xs" | "small" | "medium" | "large";
  variant?: "simple" | "raised";
  className?: string;
}

interface SizeConfig {
  container: string;
  circles: [string, string, string, string];
  puck: string;
  leaf: string;
}

const SIZES: Record<NonNullable<ThriveLogoProps["size"]>, SizeConfig> = {
  // Compact mark for nav headers / chrome, rings stay gently outside a
  // ~32px puck without overpowering adjacent wordmarks.
  xs: {
    container: "w-11 h-11",
    circles: ["w-11 h-11", "w-10 h-10", "w-9 h-9", "w-8 h-8"],
    puck: "w-8 h-8",
    leaf: "w-5 h-5",
  },
  small: {
    container: "w-24 h-24",
    circles: ["w-24 h-24", "w-20 h-20", "w-16 h-16", "w-12 h-12"],
    puck: "w-12 h-12",
    leaf: "w-7 h-7",
  },
  medium: {
    container: "w-32 h-32",
    circles: ["w-32 h-32", "w-28 h-28", "w-24 h-24", "w-20 h-20"],
    puck: "w-16 h-16",
    leaf: "w-10 h-10",
  },
  large: {
    container: "w-40 h-40",
    circles: ["w-40 h-40", "w-36 h-36", "w-32 h-32", "w-28 h-28"],
    puck: "w-24 h-24",
    leaf: "w-14 h-14",
  },
};

const RING_BORDERS = [
  "border-teal-200/10",
  "border-border-primary/15",
  "border-teal-400/20",
  "border-teal-500/25",
] as const;

const RING_DURATIONS = ["6s", "5.5s", "5s", "4.5s"] as const;
const RING_DELAYS = ["0s", "0.8s", "1.6s", "2.4s"] as const;

export function ThriveLogo({ size = "medium", variant = "simple", className }: ThriveLogoProps) {
  const config = SIZES[size];

  // At xs the puck sits inside UI chrome (nav headers), so a soft drop
  // shadow reads better than the hero-sized shadow-xl.
  const puckShadow =
    variant === "raised" ? "shadow-2xl" : size === "xs" ? "shadow-sm" : "shadow-xl";

  const puckClasses = cn(
    config.puck,
    "bg-surface-elevated rounded-full flex items-center justify-center relative z-10",
    puckShadow,
  );

  const puckStyle: CSSProperties | undefined =
    variant === "raised"
      ? {
          background:
            "linear-gradient(145deg, white 0%, color-mix(in srgb, white 80%, transparent) 100%)",
          boxShadow:
            "inset 0 1px 3px color-mix(in srgb, white 60%, transparent), inset 0 -1px 3px color-mix(in srgb, var(--color-scrim) 10%, transparent), 0 4px 20px color-mix(in srgb, var(--color-scrim) 15%, transparent)",
        }
      : undefined;

  return (
    <div className={cn("relative flex items-center justify-center", config.container, className)}>
      {config.circles.map((circleClass, i) => (
        <div
          key={circleClass}
          className="absolute inset-0 flex items-center justify-center"
          aria-hidden="true"
        >
          <div
            className={cn("rounded-full border", circleClass, RING_BORDERS[i])}
            style={{
              animation: `pulse-outward ${RING_DURATIONS[i]} ease-in-out infinite`,
              animationDelay: RING_DELAYS[i],
            }}
          />
        </div>
      ))}

      <div className={puckClasses} style={puckStyle}>
        {variant === "raised" && (
          <div className="absolute inset-0 rounded-full bg-linear-to-br from-ink-on-media/20 to-transparent" />
        )}
        <ThriveLeaf className={cn(config.leaf, "relative z-20 text-ink-brand-light")} />
      </div>
    </div>
  );
}
